function getCartSectionsToRender() {
  return [
    {
      id: document.querySelector('#main-cart-items')?.dataset.id,
      selector: '#main-cart-items'
    },
    {
      id: document.querySelector('#main-cart-footer')?.dataset.id,
      selector: '#main-cart-footer'
    },
    {
      id: 'cart-drawer',
      selector: '#shopify-section-cart-drawer'
    },
    {
      id: 'cart-icon-bubble',
      selector: '#cart-icon-bubble'
    }
  ].filter((section) => section.id && document.querySelector(section.selector));
}

function replaceCartSection(sectionHtml, selector) {
  if (!sectionHtml) return;

  const currentElement = document.querySelector(selector);
  if (!currentElement) return;

  const wasDrawerOpen =
    selector === '#shopify-section-cart-drawer' &&
    document.querySelector('cart-drawer')?.classList.contains('active');

  const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
  const newElement = doc.querySelector(selector);

  if (!newElement) return;

  if (selector === '#cart-icon-bubble') {
    currentElement.replaceWith(newElement);
    return;
  }

  if (selector === '#shopify-section-cart-drawer') {
    const newDrawer = newElement.querySelector('cart-drawer');

    if (wasDrawerOpen && newDrawer) {
      newDrawer.classList.add('active');
      newDrawer.querySelector('#CartDrawer')?.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
    }

    currentElement.replaceWith(newElement);
    return;
  }

  currentElement.innerHTML = newElement.innerHTML;
}

function renderCartSections(sectionsHtml, sectionsToRender) {
  if (!sectionsHtml) return;

  sectionsToRender.forEach((section) => {
    replaceCartSection(sectionsHtml[section.id], section.selector);
  });
}

function toggleEmptyCartState(cart) {
  const isEmpty = cart.item_count === 0;

  document.querySelector('cart-items')?.classList.toggle('is-empty', isEmpty);
  document.querySelector('cart-drawer')?.classList.toggle('is-empty', isEmpty);
  document.querySelector('cart-drawer-items')?.classList.toggle('is-empty', isEmpty);
  document.querySelector('#main-cart-footer')?.classList.toggle('is-empty', isEmpty);
}

function dispatchCartUpdated(cart) {
  document.dispatchEvent(
    new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { cart }
    })
  );
}

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends HTMLElement {
      constructor() {
        super();
        this.bindEvents();
      }

      bindEvents() {
        this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));

        this.querySelectorAll('.drawer__close, [data-cart-drawer-close]').forEach((button) => {
          button.addEventListener('click', this.close.bind(this));
        });
        document.querySelector(".header__icon--cart").addEventListener("click",(e)=>{
            e.preventDefault();
            this.open();
        })
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.close();
        });
      }

      open() {
        this.classList.add('active');
        this.querySelector('#CartDrawer')?.setAttribute('aria-hidden', 'false');
        document.body.classList.add('overflow-hidden');
      }

      close() {
        this.classList.remove('active');
        this.querySelector('#CartDrawer')?.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('overflow-hidden');
      }
    }
  );
}

if (!customElements.get('quantity-input')) {
  customElements.define(
    'quantity-input',
    class QuantityInput extends HTMLElement {
      constructor() {
        super();

        this.input = this.querySelector('.quantity__input');
        this.minus = this.querySelector('button[name="minus"]');
        this.plus = this.querySelector('button[name="plus"]');

        if (!this.input) return;

        this.isUpdating = false;
        this.previousValue = Number(this.input.value) || 1;

        this.minus?.addEventListener('click', this.onButtonClick.bind(this));
        this.plus?.addEventListener('click', this.onButtonClick.bind(this));
        this.input.addEventListener('change', this.onInputChange.bind(this));
      }

      onButtonClick(event) {
        event.preventDefault();

        if (this.isUpdating) return;

        const oldValue = Number(this.input.value) || 1;
        const min = Number(this.input.getAttribute('min')) || 0;
        const max = Number(this.input.getAttribute('max')) || Infinity;
        const step = Number(this.input.getAttribute('step')) || 1;

        let newValue = oldValue;

        if (event.currentTarget.name === 'plus') newValue += step;
        if (event.currentTarget.name === 'minus') newValue -= step;

        newValue = Math.max(min, Math.min(max, newValue));

        if (newValue === oldValue) return;

        this.previousValue = oldValue;
        this.input.value = newValue;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      async onInputChange() {
        if (this.isUpdating) return;

        const line = Number(this.input.dataset.index);
        const requestedQuantity = Number(this.input.value);

        if (!line || Number.isNaN(requestedQuantity)) return;

        const previousValue = this.previousValue;
        const sectionsToRender = getCartSectionsToRender();

        this.setLoading(true);
        this.clearError();

        try {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              line,
              quantity: requestedQuantity,
              sections: sectionsToRender.map((section) => section.id),
              sections_url: window.location.pathname
            })
          });

          const cart = await response.json();

          if (!response.ok) {
            this.input.value = previousValue;
            throw new Error(cart.description || cart.message || 'Cart update failed');
          }

          const updatedItem = cart.items[line - 1];

          if (updatedItem && updatedItem.quantity !== requestedQuantity) {
            this.input.value = updatedItem.quantity;
            this.previousValue = updatedItem.quantity;
            this.showError(`Only ${updatedItem.quantity} items were added to your cart due to availability.`);
          } else {
            this.previousValue = requestedQuantity;
          }

          renderCartSections(cart.sections, sectionsToRender);
          toggleEmptyCartState(cart);
          dispatchCartUpdated(cart);
        } catch (error) {
          this.input.value = previousValue;
          this.showError(error.message);
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(isLoading) {
        this.isUpdating = isLoading;

        const row = this.closest('.cart-item');
        row?.classList.toggle('is-loading', isLoading);

        this.input.disabled = isLoading;
        if (this.minus) this.minus.disabled = isLoading;
        if (this.plus) this.plus.disabled = isLoading;
      }

      showError(message) {
        const row = this.closest('.cart-item');
        const errorText = row?.querySelector('.cart-item__error-text');

        if (errorText) errorText.textContent = message;
      }

      clearError() {
        const row = this.closest('.cart-item');
        const errorText = row?.querySelector('.cart-item__error-text');

        if (errorText) errorText.textContent = '';
      }
    }
  );
}

if (!customElements.get('cart-remove-button')) {
  customElements.define(
    'cart-remove-button',
    class CartRemoveButton extends HTMLElement {
      constructor() {
        super();

        this.button = this.querySelector('a, button');
        this.isUpdating = false;

        this.button?.addEventListener('click', this.onRemove.bind(this));
      }

      async onRemove(event) {
        event.preventDefault();

        if (this.isUpdating) return;

        const line = Number(this.dataset.index);
        if (!line) return;

        const sectionsToRender = getCartSectionsToRender();

        this.setLoading(true);

        try {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              line,
              quantity: 0,
              sections: sectionsToRender.map((section) => section.id),
              sections_url: window.location.pathname
            })
          });

          const cart = await response.json();

          if (!response.ok) {
            throw new Error(cart.description || cart.message || 'Cart update failed');
          }

          renderCartSections(cart.sections, sectionsToRender);
          toggleEmptyCartState(cart);
          dispatchCartUpdated(cart);
        } catch (error) {
          console.error(error);
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(isLoading) {
        this.isUpdating = isLoading;

        const row = this.closest('.cart-item');
        row?.classList.toggle('is-loading', isLoading);

        if (this.button) {
          this.button.disabled = isLoading;
          this.button.style.pointerEvents = isLoading ? 'none' : '';
        }
      }
    }
  );
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"][name="add"]');
        this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
        this.errorMessage = this.querySelector('.product-form__error-message');
        this.hiddenId = this.querySelector("[name=id]");
        this.hiddenId.disabled = false;
        if (!this.form) return;

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      }

      async onSubmitHandler(event) {
        event.preventDefault();

        if (this.submitButton?.classList.contains('loading')) return;

        const sectionsToRender = getCartSectionsToRender();
        const formData = new FormData(this.form);

        formData.append('sections', sectionsToRender.map((section) => section.id).join(','));
        formData.append('sections_url', window.location.pathname);

        this.clearError();
        this.setLoading(true);

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              Accept: 'application/json'
            },
            body: formData
          });

          const item = await response.json();

          if (!response.ok) {
            throw new Error(item.description || item.message || 'Add to cart failed');
          }

          renderCartSections(item.sections, sectionsToRender);

          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();

          toggleEmptyCartState(cart);
          dispatchCartUpdated(cart);

          this.openCartDrawerOrRedirect();
        } catch (error) {
          this.showError(error.message);
        } finally {
          this.setLoading(false);
        }
      }

      openCartDrawerOrRedirect() {
        const cartDrawer = document.querySelector('cart-drawer');

        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
          return;
        }

        window.location.href = '/cart';
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;

        this.submitButton.disabled = isLoading;
        this.submitButton.classList.toggle('loading', isLoading);

        const spinner = this.submitButton.querySelector('.loading-overlay__spinner');

        if (spinner) {
          spinner.classList.toggle('hidden', !isLoading);
        }
      }

      showError(message) {
        if (this.errorWrapper) this.errorWrapper.hidden = false;
        if (this.errorMessage) this.errorMessage.textContent = message;

        const buttonError = this.submitButton?.querySelector('.main-atc__error');

        if (buttonError) {
          buttonError.textContent = message;
          buttonError.style.display = '';
        }
      }

      clearError() {
        if (this.errorWrapper) this.errorWrapper.hidden = true;
        if (this.errorMessage) this.errorMessage.textContent = '';

        const buttonError = this.submitButton?.querySelector('.main-atc__error');

        if (buttonError) {
          buttonError.textContent = '';
          buttonError.style.display = 'none';
        }
      }
    }
  );
}

if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('change', this.onVariantChange.bind(this));
      }

      onVariantChange() {
        this.updateOptions();
        this.updateMasterId();

        if (!this.currentVariant) {
          this.updateVariantInput('');
          this.updateButtonState(true, 'Unavailable');
          return;
        }

        this.updateVariantInput(this.currentVariant.id);
        this.updateURL();
        this.updateLabels();
        this.updateButtonState(
          !this.currentVariant.available,
          this.currentVariant.available ? 'Add to cart' : 'Sold out'
        );

        this.renderProductInfo();

        document.dispatchEvent(
          new CustomEvent('variant:changed', {
            bubbles: true,
            detail: {
              variant: this.currentVariant,
              sectionId: this.sectionId
            }
          })
        );
      }

      get sectionId() {
        return this.dataset.section || this.closest('[data-section]')?.dataset.section;
      }

      get productUrl() {
        return this.dataset.url || window.location.pathname;
      }

      updateOptions() {
        const optionGroups = Array.from(this.querySelectorAll('.product-form__input'));

        this.options = optionGroups.map((group) => {
          const checkedRadio = group.querySelector('input[type="radio"]:checked');
          if (checkedRadio) return checkedRadio.value;

          const select = group.querySelector('select');
          if (select) return select.value;

          return null;
        });
      }

      updateMasterId() {
        this.currentVariant = this.getVariantData().find((variant) => {
          return variant.options.every((option, index) => {
            return option === this.options[index];
          });
        });
      }

      updateVariantInput(variantId) {
        if (!this.sectionId) return;

        const forms = document.querySelectorAll(
          `#product-form-${this.sectionId}, #product-form-installment-${this.sectionId}`
        );

        forms.forEach((form) => {
          const input = form.querySelector('input[name="id"]');
          if (!input) return;

          input.value = variantId;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL() {
        if (!this.currentVariant) return;

        window.history.replaceState(
          {},
          '',
          `${this.productUrl}?variant=${this.currentVariant.id}`
        );
      }

      updateButtonState(disabled, text) {
        if (!this.sectionId) return;

        const form = document.querySelector(`#product-form-${this.sectionId}`);
        if (!form) return;

        const addButton = form.querySelector('[name="add"]');
        if (!addButton) return;

        addButton.disabled = disabled;
        addButton.toggleAttribute('disabled', disabled);

        const label = addButton.querySelector(
          '.main-atc__label__text, .main-atc__label, .button__label'
        );

        if (label) label.textContent = text;
      }

      updateLabels() {
        if (!this.sectionId || !this.options) return;

        this.options.forEach((value, index) => {
          const label = document.querySelector(
            `#custom-label-${this.sectionId}-${index + 1}`
          );

          if (!label) return;

          const text = label.textContent.split('-')[0].trim();
          label.textContent = `${text} - ${value}`;
        });
      }

      renderProductInfo() {
        if (!this.currentVariant || !this.sectionId) return;

        const requestedVariantId = this.currentVariant.id;
        const url = `${this.productUrl}?variant=${requestedVariantId}&section_id=${this.sectionId}`;

        fetch(url)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');

            this.updateSectionHTML(html, requestedVariantId);
          })
          .catch((error) => {
            console.error('Variant section update failed:', error);
          });
      }

      updateSectionHTML(html, requestedVariantId) {
        if (!this.currentVariant || this.currentVariant.id !== requestedVariantId) return;

        const section = document.getElementById(`shopify-section-${this.sectionId}`);
        const newSection = html.getElementById(`shopify-section-${this.sectionId}`);

        if (!section || !newSection) return;

        const selectors = [
          '.price',
          '.product__media-wrapper',
          'media-gallery',
          'product-gallery',
          '.product__info-container',
          '.product__inventory',
          '.product__sku',
          '.product-form__buttons',
          '.product-form__input',
          'variant-selects',
          'variant-radios',
          '.product__pickup-availabilities',
          'pickup-availability',
          '.shopify-payment-button'
        ];

        selectors.forEach((selector) => {
          const oldEls = section.querySelectorAll(selector);
          const newEls = newSection.querySelectorAll(selector);

          oldEls.forEach((oldEl, index) => {
            const newEl = newEls[index];
            if (!newEl) return;
            oldEl.replaceWith(newEl.cloneNode(true));
          });
        });

        this.reExecuteScripts(section);
      }

      reExecuteScripts(section) {
        section.querySelectorAll('script').forEach((oldScript) => {
          const newScript = document.createElement('script');

          Array.from(oldScript.attributes).forEach((attr) => {
            newScript.setAttribute(attr.name, attr.value);
          });

          newScript.textContent = oldScript.textContent;
          oldScript.replaceWith(newScript);
        });
      }

      getVariantData() {
        if (this.variantData) return this.variantData;

        const json = this.querySelector('script[type="application/json"]');
        this.variantData = json ? JSON.parse(json.textContent) : [];

        return this.variantData;
      }
    }
  );
}

if (!customElements.get('dynamic-dates')) {
  customElements.define(
    'dynamic-dates',
    class DynamicDates extends HTMLElement {
      constructor() {
        super();
        this.updateDates();
      }

      updateDates() {
        const dateFormat = this.dataset.dateFormat || 'mm_dd';

        const dayLabels = (this.dataset.dayLabels || 'Mon, Tue, Wed, Thu, Fri, Sat, Sun')
          .split(',')
          .map((item) => item.trim());

        const monthLabels = (this.dataset.monthLabels || 'Jan, Feb, Mar, Apr, May, Jun, Jul, Aug, Sep, Oct, Nov, Dec')
          .split(',')
          .map((item) => item.trim());

        const daysToSkip = (this.dataset.daysToSkip || '')
          .split(',')
          .map((item) => item.trim().toLowerCase())
          .filter(Boolean);

        const datesToSkip = (this.dataset.datesToSkip || '')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);

        this.querySelectorAll('[data-dynamic-date="true"]').forEach((item) => {
          const minDays = Number(item.dataset.minDays || 0);
          const maxDays = Number(item.dataset.maxDays || minDays);
          const template = item.dataset.text || '';

          const startDate = this.getFutureDate(minDays, daysToSkip, datesToSkip);
          const endDate = this.getFutureDate(maxDays, daysToSkip, datesToSkip);

          const startText = this.formatDate(startDate, dateFormat, dayLabels, monthLabels);
          const endText = this.formatDate(endDate, dateFormat, dayLabels, monthLabels);

          item.innerHTML = template
            .replace(/\[start_date\]/g, startText)
            .replace(/\[end_date\]/g, endText);
        });
      }

      getFutureDate(daysToAdd, daysToSkip, datesToSkip) {
        const date = new Date();
        let addedDays = 0;

        while (addedDays < daysToAdd) {
          date.setDate(date.getDate() + 1);

          if (this.shouldSkipDate(date, daysToSkip, datesToSkip)) {
            continue;
          }

          addedDays++;
        }

        while (this.shouldSkipDate(date, daysToSkip, datesToSkip)) {
          date.setDate(date.getDate() + 1);
        }

        return date;
      }

      shouldSkipDate(date, daysToSkip, datesToSkip) {
        const dayName = date.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();

        const fullDate = [
          date.getFullYear(),
          String(date.getMonth() + 1).padStart(2, '0'),
          String(date.getDate()).padStart(2, '0')
        ].join('-');

        return daysToSkip.includes(dayName) || datesToSkip.includes(fullDate);
      }

      formatDate(date, format, dayLabels, monthLabels) {
        const dayIndex = date.getDay() === 0 ? 6 : date.getDay() - 1;
        const dayName = dayLabels[dayIndex] || '';
        const monthName = monthLabels[date.getMonth()] || '';
        const day = date.getDate();
        const month = date.getMonth() + 1;

        if (format === 'dd_mm') return `${day} ${monthName}`;
        if (format === 'day_dd_mm') return `${dayName}, ${day} ${monthName}`;
        if (format === 'mm_dd') return `${monthName} ${this.ordinal(day)}`;
        if (format === 'day_mm_dd') return `${dayName}, ${monthName} ${this.ordinal(day)}`;

        return `${monthName} ${this.ordinal(day)}`;
      }

      ordinal(number) {
        const suffixes = ['th', 'st', 'nd', 'rd'];
        const value = number % 100;

        return number + (suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]);
      }
    }
  );
}